import React, {useContext, useRef, useState} from 'react';
import {AgGridReact} from "ag-grid-react";
import {v4 as uuidv4} from 'uuid';
import {AgentContext} from "../../context/agent-context.jsx";
import GenericEditRemoveCellRenderer from "./generic-edit-remove-cell-renderer.jsx";
import ConfirmationDeleteList from "../../../../../../components/utils/confirmation-delete-lists.jsx";
import {FaPlus} from "react-icons/fa";
import Swal from "sweetalert2";
import {Card} from "../../../../../../components/card/card.jsx";
import Button from "../../../../../../components/button/button.jsx";
import {Modal} from "../../../../../../components/modal/modal.jsx";
import InputForm from "../../../../../../components/input/input-form.jsx";
import aggridUtils from "../../../../../../utils/aggrid-utils.js";
import projectUtils from "../../../../../../utils/project-utils.js";

const initialData = {
    itemId: null,
    weaponNumber: "",
    species: "",
    caliber: "",
    sinarm: ""
}

const PrivateWeapons = () => {
    const gridRef = useRef();
    const {user, setUser, setChanges, setTabSelected, setCpfFocus} = useContext(AgentContext)
    const [data, setData] = useState(initialData)
    const [open, setOpen] = useState(false);

    const remove = (data) => {
        ConfirmationDeleteList(() => {
            setUser(prev => ({
                ...prev,
                privateWeapons: (prev.privateWeapons ?? []).filter(weapon => weapon.itemId !== data.itemId)
            }));

            setChanges('private_weapons');
        })
    }

    const [columnDefs] = useState([
        {headerName: "Número da arma", field: "weaponNumber", flex: 1, comparator: aggridUtils.nullLastComparator},
        {headerName: "Espécie", field: "species", flex: 1, comparator: aggridUtils.nullLastComparator},
        {headerName: "Calibre", field: "caliber", flex: 1, comparator: aggridUtils.nullLastComparator},
        {headerName: "SINARM", field: "sinarm", flex: 1, comparator: aggridUtils.nullLastComparator},
        {
            headerName: "", field: "", width: 100,
            cellRenderer: GenericEditRemoveCellRenderer,
            cellRendererParams: {setData, setOpen, remove}
        },
    ]);

    const handleInputs = ({target: {value, id}}) => setData(prev => ({...prev, [id]: value}));

    const save = () => {
        const isEmpty = !data.weaponNumber.trim() && !data.species.trim() && !data.caliber.trim() && !data.sinarm.trim();
        if (isEmpty) {
            projectUtils.swalErrorMessage("", "Preencha ao menos um campo para adicionar a arma.", "Ok");
            return;
        }

        if (data.itemId) {
            setUser(prev => ({
                ...prev,
                privateWeapons: (prev.privateWeapons ?? []).map(weapon =>
                    weapon.itemId === data.itemId ? data : weapon
                )
            }));
        } else {
            data.itemId = uuidv4();
            setUser(prev => ({...prev, privateWeapons: [...(prev.privateWeapons ?? []), data]}))
        }

        setChanges('private_weapons');
        onHide();
    }

    const onHide = () => {
        setData(initialData);
        setOpen(false)
    }

    const handleClick = () => {
        if (!user.general.cpf) {
            Swal.fire({
                title: "Atenção",
                text: "Informe o CPF para incluir armas particulares.",
                icon: "warning",
                confirmButtonText: "Continuar",
                confirmButtonColor: "#063A6C",
                cancelButtonText: "Cancelar",
                showCancelButton: true,
                allowOutsideClick: false,
                allowEscapeKey: false,
            }).then(({isConfirmed}) => {
                if (isConfirmed) {
                    setTabSelected("general")
                    setCpfFocus(true)
                }
            });
        } else {
            setOpen(true)
        }
    }

    return (
        <>
            <Card>
                <Card.Header className={"gap-2"}>
                    <Card.Title>Armas particulares</Card.Title>
                    <Button className={"h-[30px] w-[30px] !p-0"} onClick={handleClick}>
                        <FaPlus />
                    </Button>
                </Card.Header>
                <Card.Body>
                    <div className={"h-[calc(100vh-196px)]"}>
                        <AgGridReact
                            ref={gridRef}
                            rowData={user?.privateWeapons ?? []}
                            columnDefs={columnDefs}
                            animateRows={false}
                            suppressCellFocus={true}
                            gridOptions={{localeText: aggridUtils.localization()}}
                            theme={aggridUtils.generalTheme()}
                        />
                    </div>
                </Card.Body>
            </Card>

            <Modal isCloseClick={false} open={open} setOpen={onHide} className={"w-8/12"}
                   title={data.itemId ? "Editar arma particular" : "Adicionar arma particular"} overflow={true}>
                <Modal.Body className="p-2">
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-3">
                            <InputForm id="weaponNumber" value={data.weaponNumber} label="Número da arma" onChange={handleInputs}/>
                        </div>
                        <div className="col-span-3">
                            <InputForm id="species" value={data.species} label="Espécie" onChange={handleInputs}/>
                        </div>
                        <div className="col-span-3">
                            <InputForm id="caliber" value={data.caliber} label="Calibre" onChange={handleInputs}/>
                        </div>
                        <div className="col-span-3">
                            <InputForm id="sinarm" value={data.sinarm} label="SINARM" onChange={handleInputs}/>
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Footer className={"p-2"}>
                    <div className={"w-full flex justify-end"}>
                        <Button onClick={save}>
                            Confirmar
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default PrivateWeapons;
